import type { DriveStep } from 'driver.js';

// New in Salesforce section tour
export const pipelineNewTourSteps: DriveStep[] = [
  {
    element: '[data-onboarding="section-new"]',
    popover: {
      title: 'New in Salesforce',
      description: 'This section shows cases from the Salesforce Translation queue that haven\'t been downloaded yet. These are ready to be processed.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="case-table"]',
    popover: {
      title: 'Available Cases',
      description: 'Cases here show: Case Number, Type of Analysis, and Last Modified date in Salesforce. Select cases to download them.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="download-btn"]',
    popover: {
      title: 'Download Action',
      description: 'Click to download selected cases. This fetches the PDF files from Salesforce and prepares them for translation.',
      side: 'bottom',
      align: 'start',
    },
  },
];

// Queue section tour
export const pipelineQueueTourSteps: DriveStep[] = [
  {
    element: '[data-onboarding="section-queue"]',
    popover: {
      title: 'Translation Queue',
      description: 'This is where the main work happens. Cases here are at various stages: downloaded, translating, translated, generating PDF, or ready for review.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="case-table"]',
    popover: {
      title: 'Queue Cases',
      description: 'Cases show: Case Number, Type, Page Count, Status (color-coded), and Last Updated. Failed cases also appear here with error details.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="translate-btn"]',
    popover: {
      title: 'Translate Action',
      description: 'Select downloaded cases and click to start translation. The AI will process the PDF and generate an English translation.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="review-link"]',
    popover: {
      title: 'Review Link',
      description: 'Click to open the case review page where you can view the original PDF side-by-side with the translation, make edits, and approve.',
      side: 'left',
      align: 'center',
    },
  },
  {
    popover: {
      title: 'Case Status Flow',
      description: 'Cases in the queue progress through these statuses:\n\n- Downloaded: Ready for translation\n- Translating: AI processing\n- Translated: Generating PDF\n- Generating PDF: Creating final document\n- Ready for Review: Needs human approval\n- Failed: Error occurred (can retry)',
      side: 'over',
      align: 'center',
    },
  },
];

// Approved section tour
export const pipelineApprovedTourSteps: DriveStep[] = [
  {
    element: '[data-onboarding="section-approved"]',
    popover: {
      title: 'Approved Cases',
      description: 'Cases here have been reviewed and approved. They\'re ready to be uploaded back to Salesforce.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="upload-btn"]',
    popover: {
      title: 'Upload Action',
      description: 'Select approved cases and click to upload them to Salesforce. The translated PDF and Word document will be attached to the case.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="unapprove-action"]',
    popover: {
      title: 'Unapprove Option',
      description: 'If you need to make changes to an approved case, you can unapprove it to send it back to the review queue.',
      side: 'left',
      align: 'center',
    },
  },
];

// Uploaded section tour
export const pipelineUploadedTourSteps: DriveStep[] = [
  {
    element: '[data-onboarding="section-uploaded"]',
    popover: {
      title: 'Uploaded Cases',
      description: 'Successfully completed cases. The translation has been uploaded to Salesforce. This is the final stage in the normal workflow.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="case-table"]',
    popover: {
      title: 'Completed Cases',
      description: 'This section uses server-side pagination since there can be many completed cases. You can see the Salesforce status and last modified date.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="pagination"]',
    popover: {
      title: 'Pagination',
      description: 'Navigate through pages of completed cases. You can also change the page size (10, 25, or 50 per page).',
      side: 'top',
      align: 'center',
    },
  },
];

// Returned section tour
export const pipelineReturnedTourSteps: DriveStep[] = [
  {
    element: '[data-onboarding="section-returned"]',
    popover: {
      title: 'Returned Cases',
      description: 'These are cases that were uploaded but reappeared in Salesforce\'s Translation queue. This usually means someone in Salesforce moved the case back.',
      side: 'right',
      align: 'start',
    },
  },
  {
    popover: {
      title: 'Why Cases Get Returned',
      description: 'Common reasons:\n\n- Quality issues with the translation\n- Additional documents were added\n- Wrong file was translated\n- Salesforce workflow moved it back\n\nYou\'ll need to review these cases and decide whether to restart the translation.',
      side: 'over',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="restart-btn"]',
    popover: {
      title: 'Restart Action',
      description: 'Click to delete all local files and re-download from Salesforce. This gives you a fresh start with the current version of the case files. Case history is preserved.',
      side: 'left',
      align: 'center',
    },
  },
];

// Issues section tour
export const pipelineIssuesTourSteps: DriveStep[] = [
  {
    element: '[data-onboarding="section-issues"]',
    popover: {
      title: 'Issues',
      description: 'Cases here have sync problems with Salesforce. There are two types: "Lost" and "Found" cases.',
      side: 'right',
      align: 'start',
    },
  },
  {
    popover: {
      title: 'Lost Cases',
      description: 'A case is "Lost" when:\n\n- It was in our system being processed\n- It disappeared from Salesforce\'s Translation queue\n- But we never uploaded it\n\nThis usually means someone moved the case out of the Translation queue in Salesforce before we finished.',
      side: 'over',
      align: 'center',
    },
  },
  {
    popover: {
      title: 'Found Cases',
      description: 'A case is "Found" when:\n\n- It was previously marked as Lost\n- It reappeared in Salesforce\'s Translation queue\n\nThis is a recovery state - the case can now be processed again.',
      side: 'over',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="issue-filter"]',
    popover: {
      title: 'Filter by Issue Type',
      description: 'Use this filter to show only Lost cases, only Found cases, or all issues.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="restart-btn"]',
    popover: {
      title: 'Restart to Recover',
      description: 'For both Lost and Found cases, the Restart action lets you re-download from Salesforce and start fresh.',
      side: 'left',
      align: 'center',
    },
  },
];
